import { Trans } from "@lingui/macro";
import FindYourCruise from "components/FindYourCruise";
import { useFYC } from "components/FindYourCruise/useFYC";
import OfferHighlights, {
  OfferHighlight
} from "components/OfferHighlights/OfferHighlights";
import {
  Button,
  SectionContainer,
  OpeningText,
  OpeningTextHeading,
  OpeningTextSubHeading,
  HeroBanner
} from "@silversea-ssc/ui-components-react";
import React from "react";
import getUrlsByLanguage from "src/urls/urls";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import { defaultLocalizedOffer, getCruisePerimeterAlgoliaQuery } from "./utils";
import { OfferFromApi, OfferTokenValues } from "types/api/exclusive_offers.d";
import OfferTokenizedContent from "components/OfferTokenizedContent/OfferTokenizedContent";

import DateOnlyFormat from "components/DateOnlyFormat";
import Uppercase from "components/Text/Uppercase";
import {
  OfferPerAirPromoQuery,
  OfferPerPromoQuery,
  SanityMultilingualBlockContent,
  SanityMultilingualString
} from "types/graphql-types";
import SanityBuilderToComponents, {
  SanityBuilderSectionType
} from "components/PageBuilder";
import { ButtonsContainer } from "@silversea-ssc/ui-components-react";
import PageBuilderPageCTAButton from "components/PageBuilder/PageBuilderPageCTAButton";
import OpenGraph from "components/SEO/OpenGraph";
import { WindowLocation } from "@reach/router";
import { NOT_FOUND, UseFetchDataResponse } from "../../utils/useFetchApi";
import FYCQueryBuilder from "components/FindYourCruise/FYCQueryBuilder";
import { TextStyle } from "@silversea-ssc/ui-components-react";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";

export interface LocalizedOffer {
  title?: SanityMultilingualString | SanityMultilingualBlockContent;
  highlightsTitle?: SanityMultilingualString;
  offerHighlights: OfferHighlight[];
  termsAndConditionsFootnote?:
    | SanityMultilingualString
    | SanityMultilingualBlockContent;
  data?: OfferFromApi;
}

export interface OfferPageProps {
  offer: OfferPerPromoQuery["offer"] | OfferPerAirPromoQuery["offer"];
  localizedOfferResponse: UseFetchDataResponse<LocalizedOffer>;
  location: WindowLocation;
}

const offerTitle = (
  title?: SanityMultilingualBlockContent | SanityMultilingualString,
  expiresOn?: string,
  tokenValues?: OfferTokenValues
) => (
  <OpeningText>
    <OpeningTextHeading>
      <OfferTokenizedContent
        content={title?.localized}
        tokenValues={tokenValues || {}}
      />
    </OpeningTextHeading>
    {expiresOn && (
      <OpeningTextSubHeading>
        <Uppercase>
          <Trans>Offer expires on</Trans>{" "}
          <DateOnlyFormat
            value={expiresOn}
            format={{
              year: "numeric",
              month: "long",
              day: "numeric"
            }}
          />
        </Uppercase>
      </OpeningTextSubHeading>
    )}
  </OpeningText>
);

const offerNotAvailable = () => (
  <OpeningText>
    <OpeningTextHeading>
      <Trans>This exclusive offer is not available in your country</Trans>
    </OpeningTextHeading>
  </OpeningText>
);

const OfferPage: React.FC<OfferPageProps> = ({
  offer,
  localizedOfferResponse: {
    data: {
      title,
      highlightsTitle,
      offerHighlights,
      termsAndConditionsFootnote,
      data
    } = defaultLocalizedOffer,
    status,
    statusCode
  },
  location
}) => {
  const language = useLanguage();
  const urls = getUrlsByLanguage(language.code);
  const geolocation = useGeolocation();
  const algoliaQuery = React.useMemo(() => {
    return getCruisePerimeterAlgoliaQuery(
      new FYCQueryBuilder(),
      data
    ).generate();
  }, [data]);

  const {
    searchClient,
    filterDefaultSearch,
    searchState,
    createURL,
    onSearchStateChange,
    localizedSearchIndexName
  } = useFYC(algoliaQuery, geolocation.defaultCurrency);

  if (!offer) return null;

  const toDate = data?.toDate;
  const expiresOn = toDate?.type == "givenDate" ? toDate.date : undefined;
  return (
    <React.Fragment>
      <OpenGraph
        openGraph={offer.metadata?.openGraph}
        altImage={offer.heroImage?.asset?.url}
        excludeFromSitemap={offer.metadata?.excludeFromSitemap}
      />
      <HeroBanner background={offer.heroImage} size="compact" />
      <SectionContainer
        spacingTop="compact"
        spacingBottom={null}
        dataContentName="offer_title"
      >
        {status === "success" &&
          offerTitle(title, expiresOn, data?.tokenValues)}
        {status === "error" && statusCode === NOT_FOUND && offerNotAvailable()}
      </SectionContainer>
      <SectionContainer
        spacingTop="compact"
        spacingBottom={null}
        dataContentName="offer_highlights"
      >
        <OfferHighlights
          variant="large"
          title={highlightsTitle?.localized}
          offers={offerHighlights}
          tokenValues={data?.tokenValues}
        />
      </SectionContainer>
      {status === "success" && (
        <>
          <SectionContainer
            spacingTop="compact"
            spacingBottom="compact"
            dataContentName="offer_details"
          >
            <TextStyle fontSize="large" textAlign="center" boxed>
              <OfferTokenizedContent
                content={offer.description?.localized}
                tokenValues={data?.tokenValues}
              />
            </TextStyle>

            {termsAndConditionsFootnote &&
              termsAndConditionsFootnote.localized && (
                <TextStyle fontSize="disclaimer" textAlign="center" boxed>
                  <OfferTokenizedContent
                    content={termsAndConditionsFootnote?.localized}
                    tokenValues={data?.tokenValues}
                  />
                </TextStyle>
              )}
            <ButtonsContainer spacingTop="regular">
              <Button
                variant="primary"
                href={urls.serviceLinks.raq.href}
                data-content-piece="request_a_quote"
              >
                {offer.customCTALabel ? (
                  <React.Fragment>
                    {offer.customCTALabel.localized}
                  </React.Fragment>
                ) : (
                  <Trans id="cta-request-quote-for-exclusive-offer">
                    REQUEST A QUOTE FOR AN EXCLUSIVE OFFER
                  </Trans>
                )}
              </Button>

              {offer.secondaryCTA &&
                offer.secondaryCTA?.label?.localized &&
                offer.secondaryCTA.page && (
                  <PageBuilderPageCTAButton
                    key={offer.secondaryCTA._key}
                    {...offer.secondaryCTA}
                    defaultVariant="secondary-gray"
                  />
                )}
            </ButtonsContainer>
          </SectionContainer>
          {offer.sections && offer.sections?.length > 0 && (
            <SanityBuilderToComponents
              sections={offer.sections as SanityBuilderSectionType[]}
              initialBackgroundColor="neutral05"
              location={location}
            />
          )}
          <FindYourCruise
            showTitle={false}
            backgroundColor="neutral10"
            searchClient={searchClient}
            initialSearchQuery={filterDefaultSearch}
            resultsPageSize={8}
            defaultSearchIndexName={localizedSearchIndexName}
            searchState={searchState}
            onSearchStateChange={onSearchStateChange}
            createURL={createURL}
            showFreeTextSearch={true}
            showExpeditionFilter={true}
            specialTypeFilter={undefined}
            showDestinationFilter={true}
            showShipFilter={true}
            showDurationFilter={true}
            showOthersFilter={true}
            showPromosFilter={false}
          />
        </>
      )}
    </React.Fragment>
  );
};

export default OfferPage;
